import { type HTMLAttributes, type PropsWithChildren } from 'react';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { type SubsProduct } from '@cx-web/domain-types';
import { featureFlags, useFeatureFlag } from '@cx-web/feature-flags/provider';
import { getCurrencySymbol } from '@cx-web/utils/getCurrencySymbol';
import { Button } from '@fpsm/cxy-components/Button';
import clsx from 'clsx';
import capitalize from 'lodash.capitalize';

import { REPUBS } from '@cx-modules/constants';
import { type Repub } from '@cx-modules/types';

import { trackLandingSubscriptionClick } from '~/components/BettingInsights/tracking.ts';
import { formatPrice } from '~/configuration/utils/formatPrice.ts';

import styles from './Hero.module.scss';

export type HeroProps = {
  currentPath: string;
  hasActiveSubscription: boolean;
  hasCancelledSubscription: boolean;
  monthlyProduct?: SubsProduct;
  repub: Repub;
  repubPath: string;
  shouldHideDetailsMobile?: boolean;
  yearlyProduct?: SubsProduct;
} & PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

export const Hero = ({
  children,
  className,
  currentPath,
  hasActiveSubscription,
  hasCancelledSubscription,
  monthlyProduct,
  repub,
  repubPath,
  shouldHideDetailsMobile,
  yearlyProduct,
  ...props
}: HeroProps) => {
  const isHeroPricingEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__HERO_PRICING) && repub === REPUBS.US.name;

  return (
    <div className={clsx(styles.hero, className)} data-testid="hero-content" {...props}>
      <div
        className={clsx(styles.textWrapper, {
          [styles.hideDetailsOnMobile]: shouldHideDetailsMobile,
        })}
      >
        {children}
      </div>
      {isHeroPricingEnabled && yearlyProduct && monthlyProduct && !hasActiveSubscription && (
        <div className={styles.pricingSection}>
          {[monthlyProduct, yearlyProduct].map(product => {
            const isAnnual = product.term === SUBSCRIPTION_TERM.ANNUAL;
            const monthlyPrice = isAnnual ? Math.floor((product.price / 12) * 100) / 100 : product.price;
            return (
              <div
                className={`${styles.priceSelection} ${isAnnual ? styles.bestValueBorder : ''}`}
                data-testid={`hero-${product.term.toLowerCase()}-price`}
                key={product.id}
              >
                {isAnnual && <p className={styles.bestValueTag}>Best Value</p>}
                <p className={styles.term} data-testid="term">
                  {isAnnual ? 'Annual' : capitalize(product.term)}
                </p>
                <p className={styles.price} data-testid="price">
                  <span className={styles.monthlyCost}>
                    {getCurrencySymbol(product.currency)}
                    {formatPrice(monthlyPrice).whole}
                  </span>
                  .{formatPrice(monthlyPrice).decimal} /month
                </p>
                <p className={styles.paid} data-testid="paid-term">
                  Paid {capitalize(product.term)}
                </p>
                <Button asChild className={styles.subscribeButton} variant="secondary">
                  <a
                    href={`${repubPath}/plus?redirectTo=${currentPath}#plans`}
                    onClick={e => {
                      e.preventDefault();
                      trackLandingSubscriptionClick({
                        isUpgrade: hasCancelledSubscription,
                        price: product.priceFormatted,
                        repub,
                        selection: product.name as 'Essential' | 'Premium',
                        term: product.term,
                      });
                      window.location.href = `${repubPath}/plus?redirectTo=${currentPath}#plans`;
                    }}
                  >
                    {hasCancelledSubscription ? (
                      <>
                        <span className={styles.fullText}>Subscribe now</span>
                        <span className={styles.shortText}>Subscribe now</span>
                      </>
                    ) : (
                      <>
                        <span className={styles.fullText}>Start 7-Day Free Trial</span>
                        <span className={styles.shortText}>Start Free Trial</span>
                      </>
                    )}
                  </a>
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
